import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { FileModel, FileVersionModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../../utils/async';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { filenameToFontAwesomeIcon, toHumanReadableFileSize } from '../../../../utils/io/file';
import { AppContext } from '../../../state/app-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { FileManagerCadFileStatus, resolveLastFileVersionCadStatus } from '../../../utils/file';
import { nameof } from '../../../utils/nameof';
import { CheckBox } from '../../check-box/check-box';
import { Icon } from '../../icon/icon';
import { IntegrationsContext } from '../../integrations/state/integrations-state';
import { Table } from '../../table/table';
import { useFileVersions } from '../hooks/use-file-versions';
import { combineFoldersFiles, DraggableItem } from '../move-file-folder-helper';
import { FolderListViewContext } from '../state/folder-list-view-state';
import { TreeContext } from '../state/folder-tree-state';
import { CadStatusIcon } from './cad-status-icon';
import { WrapWithColor } from './wrap-with-color';

interface Props {
  file: FileModel;
  displayCheckboxCell?: boolean;
  displayCheckbox?: boolean;
  checked: boolean;
  selected: boolean;
  displayCanBeAddedToSkyViewColumn?: boolean;
  displaySkyMapConnectColumn: boolean;
  displaySharedColumn: boolean;
  displayTagColumn: boolean;
  checkVolatileName: boolean;
  onClick?: (file: FileModel) => void;
  onSelectionChanged: (selected: boolean, file: FileModel) => void;
  moveFileDialog?: { show: () => void; hide: () => void; visible: boolean };
  onTargetFolderChanged?: (targetFolder: { id: string; name: string }) => void;
}

function useTriggerFileVersionConversionCallback(
  lastVersion: FileVersionModel,
  refresh: () => void,
  setStatus: (status: FileManagerCadFileStatus) => void,
) {
  const callback: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();

      iiafe(async () => {
        try {
          setStatus('RUNNING');
          await SkyMapAxiosServiceFactory.instance
            .createFileVersionServiceV1()
            .createSkyMapInformationModel({
              path: {
                fileVersionId: lastVersion.id,
              },
            });
          refresh();
        } catch (err) {
          setStatus('FAILED');
        }
      });
    },
    [lastVersion, refresh, setStatus],
  );

  return callback;
}

const FileRow = React.memo(
  ({ displayCheckboxCell = true, displayCheckbox = true, ...props }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { lastVersion } = useFileVersions(props.file.versions);
    const checkBoxRef = React.useRef<HTMLInputElement>(null);
    const { sharedFolderId } = React.useContext(ProjectContext);
    const { currentUser } = React.useContext(AppContext);
    const { hasWriteAccessToCurrentFolder } = React.useContext(TreeContext);
    const { extractVolatileCharacters } = React.useContext(IntegrationsContext);
    const { checkedFiles, checkedFolders, refresh } = React.useContext(FolderListViewContext);
    const { onSelectionChanged: propsOnSelectionChanged } = props;
    const [canBeAddedToSkyViewStatus, setCanBeAddedToSkyViewStatus] =
      useState<FileManagerCadFileStatus>();
    const triggerFileConversion = useTriggerFileVersionConversionCallback(
      lastVersion,
      refresh,
      setCanBeAddedToSkyViewStatus,
    );

    useEffect(() => {
      setCanBeAddedToSkyViewStatus(
        props.displayCanBeAddedToSkyViewColumn
          ? resolveLastFileVersionCadStatus(props.file)
          : undefined,
      );
    }, [props.displayCanBeAddedToSkyViewColumn, props.file]);

    const onCheckBoxCellClick = () => {
      checkBoxRef.current?.click();
    };

    const onCheckBoxChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        propsOnSelectionChanged(e.target.checked, props.file);
      },
      [props.file, propsOnSelectionChanged],
    );

    const [, dragRef, dragPreviewRef] = useDrag(
      () => ({
        type: 'File',
        item: {
          selectedEntities: combineFoldersFiles(checkedFolders, checkedFiles),
          dialog: props.moveFileDialog,
          setTargetFolder: props.onTargetFolderChanged,
        } as DraggableItem,
        collect: (monitor) => ({
          opacity: monitor.isDragging() ? 0.5 : 1,
        }),
        canDrag: hasWriteAccessToCurrentFolder(currentUser),
      }),
      [checkedFiles, checkedFolders],
    );

    useEffect(() => {
      dragPreviewRef(getEmptyImage(), { captureDraggingState: true });
    }, [dragPreviewRef]);

    const { icon, color } = filenameToFontAwesomeIcon(props.file.name);
    const volatileCharacter = extractVolatileCharacters(props.file.name);

    return (
      <>
        <Component
          checked={props.checked}
          ref={props.checked ? dragRef : null}
          selected={props.selected}
          onClick={() => props.onClick?.(props.file)}
        >
          {displayCheckboxCell && !sharedFolderId && (
            <Table.Cell
              padding={{ left: 0.7, right: 0.7 }}
              onClick={displayCheckbox ? onCheckBoxCellClick : undefined}
              onHoverBackgroundColor={
                displayCheckbox
                  ? props.checked || props.selected
                    ? theme.color.brand.medium
                    : '#F0F0F0'
                  : undefined
              }
            >
              {displayCheckbox && (
                <CheckBox checked={props.checked} ref={checkBoxRef} onChange={onCheckBoxChange} />
              )}
            </Table.Cell>
          )}

          <Table.Cell padding={{ left: 0.4 }}>
            <NameContainer>
              <Icon color={color} data-testid="icon-file" fixedWidth={true} icon={['fad', icon]} />
              <span>{props.file.name}</span>
            </NameContainer>
          </Table.Cell>

          {canBeAddedToSkyViewStatus && (
            <Table.Cell shrink={true}>
              <CadStatusIcon
                existsInSkyView={(props.file.dynamicAssets?.length ?? 0) > 0}
                status={canBeAddedToSkyViewStatus}
                onTriggerFileConversion={triggerFileConversion}
              />
            </Table.Cell>
          )}

          {props.displaySkyMapConnectColumn && <Table.Cell wrapping={'none'} />}

          {props.checkVolatileName && (
            <Table.Cell wrapping={'none'}>
              {volatileCharacter.length > 0 && (
                <Icon
                  icon={['fad', 'exclamation-triangle']}
                  style={{ color: theme.color.red }}
                  swapOpacity={true}
                  title={t('fileManager.folderListView.invalidSkyMapConnectCharacters', {
                    ns: 'components',
                    volatileCharacters: volatileCharacter.join(' '),
                  }).replaceAll('\\n', '\n')}
                />
              )}
            </Table.Cell>
          )}

          {props.displaySharedColumn && <Table.Cell wrapping={'none'} />}

          {props.displayTagColumn && <Table.Cell wrapping={'none'} />}

          <Table.Cell wrapping={'none'}>
            {t('fileManager.folderListView.fileType', { ns: 'components' })}
          </Table.Cell>

          <Table.Cell monospaced="montserrat" textAlign="right" wrapping={'none'}>
            {toHumanReadableFileSize(lastVersion.size)}
          </Table.Cell>

          <Table.Cell wrapping={'none'}>
            <Trans
              components={{ color: <WrapWithColor /> }}
              i18nKey="fileManager.folderListView.numFileVersions"
              ns="components"
              values={{
                count: props.file.versions.length,
              }}
            />
          </Table.Cell>

          <Table.Cell monospaced="montserrat" wrapping={'none'}>
            {formattedDateTime(lastVersion.createdAt)}
          </Table.Cell>
        </Component>
      </>
    );
  },
);

FileRow.displayName = nameof({ FileRow });

const Component = styled(Table.Row)``;

const NameContainer = styled.div`
  display: flex;
  gap: 0.75em;
  align-items: center;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  span {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
  }
`;

export { FileRow };
